import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { collection, limit, onSnapshot, orderBy, query, where } from "firebase/firestore";
import { dbAdmin } from "../firebaseConfig";
import { FiShare2 } from "react-icons/fi";
import "../styles/MasjidKempen.css";

const fmtRM = (n) => {
    const x = Number(n || 0);
    return `RM${x.toLocaleString("ms-MY")}`;
};

export default function MasjidKempen() {
    const { slug } = useParams();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [masjid, setMasjid] = useState(null);

    const [jumlahByKempen, setJumlahByKempen] = useState(new Map());

    // load masjid by slug
    useEffect(() => {
        if (!slug) return;

        const s = String(slug).trim().toLowerCase();
        setLoading(true);

        const qRef = query(
            collection(dbAdmin, "masajid"),
            where("profail.slug", "==", s),
            limit(1)
        );

        const unsub = onSnapshot(
            qRef,
            (snap) => {
                if (snap.empty) {
                    setMasjid(null);
                    setLoading(false);
                    return;
                }
                const d = snap.docs[0];
                setMasjid({ id: d.id, ...d.data() });
                setLoading(false);
            },
            () => {
                setMasjid(null);
                setLoading(false);
            }
        );

        return () => unsub();
    }, [slug]);

    // optional totals
    useEffect(() => {
        const masjidId = masjid?.id;
        if (!masjidId) return;

        const qRef = query(
            collection(dbAdmin, "masajid", masjidId, "derma_rekod"),
            orderBy("createdAt", "desc"),
            limit(200)
        );

        const unsub = onSnapshot(
            qRef,
            (snap) => {
                const map = new Map();
                snap.docs.forEach((d) => {
                    const r = d.data() || {};
                    const kempenId = r.kempenId || r.campaignId || null;
                    if (!kempenId) return;

                    const amaun = Number(r.amaun ?? r.amount ?? 0) || 0;
                    const status = String(r.status || "").toLowerCase();
                    const ok = status === "berjaya" || status === "paid" || status === "success";
                    if (!ok) return;

                    map.set(kempenId, (map.get(kempenId) || 0) + amaun);
                });
                setJumlahByKempen(map);
            },
            () => setJumlahByKempen(new Map())
        );

        return () => unsub();
    }, [masjid?.id]);

    const list = useMemo(() => {
        const raw = Array.isArray(masjid?.kempen?.senarai) ? masjid.kempen.senarai : [];
        return raw
            .filter((k) => k && k.aktif !== false)
            .sort((a, b) => (b?.updatedAt || b?.createdAt || 0) - (a?.updatedAt || a?.createdAt || 0));
    }, [masjid]);

    const bannerImg = masjid?.profail?.gambar || masjid?.gambar || masjid?.profail?.logo || "";

    const shareKempen = async (k) => {
        const url = `${window.location.origin}/${slug}/kempen/${k.id}`;
        const title = k?.tajuk || "Kempen Derma";

        try {
            if (navigator.share) {
                await navigator.share({ title, text: title, url });
                return;
            }
        } catch {
            // ignore, fallback copy
        }

        try {
            await navigator.clipboard.writeText(url);
            alert("Link kempen telah disalin.");
        } catch {
            prompt("Copy link kempen:", url);
        }
    };

    if (loading) {
        return (
            <div className="mk-page">

                <div className="mk-empty">Loading...</div>
            </div>
        );
    }

    if (!masjid) {
        return (
            <div className="mk-page">
                <div className="mk-empty">Masjid tidak ditemui.</div>
            </div>
        );
    }

    return (
        <div className="mk-page">
            <div className="mk-top">

                <div className="mk-title">Kempen</div>
            </div>

            {list.length === 0 ? (
                <div className="mk-empty">Tiada kempen aktif buat masa ini.</div>
            ) : (
                <div className="mk-grid">
                    {list.map((k) => {
                        const img = k.imejUrl || bannerImg || "/images/150.png";
                        const raised = Number(jumlahByKempen.get(k.id) || 0) || 0;
                        const goal = Number(k?.sasaran || 0) || 0;
                        const hasGoal = goal > 0;
                        const percent = hasGoal
                            ? Math.max(0, Math.min(100, Math.round((raised / goal) * 100)))
                            : 0;

                        return (
                            <div className="kpCard" key={k.id}>
                                <div className="kpCard-img">
                                    <img
                                        src={img}
                                        alt={k.tajuk || "Kempen"}
                                        onError={(e) => (e.currentTarget.src = bannerImg || "/images/150.png")}
                                    />
                                    <div className="kpPill">Derma</div>
                                </div>

                                <div className="kpCard-body">
                                    <div className="kpCard-title">{k.tajuk || "Kempen Derma"}</div>
                                    <div className="kpCard-desc">{k.keterangan || "Semoga sama-sama menderma"}</div>

                                    {hasGoal && (
                                        <div className="kpBox">
                                            <div className="kpBox-row">
                                                <div className="kpBox-label">Donation</div>
                                                <div className="kpBox-pct">{percent}%</div>
                                            </div>

                                            <div className="kpBar">
                                                <div className="kpBar-fill" style={{ width: `${percent}%` }} />
                                            </div>

                                            <div className="kpBox-row2">
                                                <div className="kpBox-amt">
                                                    <div className="kpBox-amtLabel">Raised:</div>
                                                    <div className="kpBox-amtValue">{fmtRM(raised)}</div>
                                                </div>

                                                <div className="kpBox-amt right">
                                                    <div className="kpBox-amtLabel">Goal:</div>
                                                    <div className="kpBox-amtValue">{fmtRM(goal)}</div>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    <div className="mk-actions">
                                        <button className="kpBtn" onClick={() => navigate(`/${slug}/kempen/${k.id}`)}>
                                            Derma Sekarang
                                        </button>

                                        <button className="kpBtn-share" onClick={() => shareKempen(k)} aria-label="Share" type="button">
                                            <FiShare2 /> Share
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );

}
